import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Empty, Flex, Row, Skeleton, Space, Tag, Typography } from "antd";
import { ArrowLeftOutlined, DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { isAuthenticated } from "../../../commons/Auth/auth";
import ContractFormModal from "./ContractFormModal";
import SignContractModal from "./SignContractModal";
import { STATUS_COLORS, STATUS_LABELS, getContractType } from "./contractTypes";
import "./Contracts.css";
import { saveFile } from "../../../native/files";

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

const ContractDetails = () => {
  const { t } = useTranslation("dashboard");
  const { contractId } = useParams();
  const history = useHistory();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [signing, setSigning] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchContract = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/contract/${contractId}`,
        { headers: authHeader() }
      );
      setContract(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.errorMessage || err.message);
    }
  };

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/pdf/${contractId}`,
        { headers: authHeader(), responseType: "blob" }
      );
      await saveFile(res.data, `${contract.title || "contract"}-${contractId}.pdf`);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return <Alert type="error" showIcon message={t(error)} className="m-3" />;
  }
  if (!contract) return <Skeleton active className="p-4" />;

  const type = getContractType(contract.contractType);
  const me = isAuthenticated();
  const isOwnContract =
    Number(me?.role) === 1 && String(contract.employee?._id || "") === String(me?._id);
  const permissions = contract.permissions || {};
  const partyName =
    [contract.employee?.firstName, contract.employee?.lastName].filter(Boolean).join(" ") ||
    contract.partyName;
  const parties = [
    {
      key: "employer",
      label: type.employment ? t("Employer") : t("Client"),
      defaultName: contract.employer?.representative,
      canSign: permissions.signEmployer,
      inPerson: false
    },
    {
      key: "employee",
      label: type.employment ? t("Employee") : t("Contractor"),
      defaultName: partyName,
      canSign: permissions.signEmployee,
      // Anyone but the employee themselves signing this side means the device is handed to
      // the contract partner; the server records it the same way.
      inPerson: !isOwnContract
    }
  ];
  const canEdit = permissions.update && contract.status !== "signed";

  return (
    <div className="contract-details">
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} className="mb-3">
        <Flex align="center" gap={8} className="contract-details__header">
          <Button
            className="contract-details__back"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.goBack()}
          />
          <div className="contract-details__header">
            <Title level={4} className="mb-0 contract-details__title">
              {contract.title || t("Contract")}
            </Title>
            <Space size={4} wrap>
              <Tag color="geekblue">{t(type.label)}</Tag>
              <Tag color={STATUS_COLORS[contract.status || "draft"]}>
                {t(STATUS_LABELS[contract.status || "draft"])}
              </Tag>
              <Text type="secondary">
                {dayjs(contract.startDate).format("DD.MM.YYYY")} –{" "}
                {contract.endDate ? dayjs(contract.endDate).format("DD.MM.YYYY") : t("open-ended")}
              </Text>
            </Space>
          </div>
        </Flex>
        <Space wrap>
          {canEdit && (
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              {t("Edit")}
            </Button>
          )}
          <Button icon={<DownloadOutlined />} loading={downloading} onClick={downloadPdf}>
            {t("Download PDF")}
          </Button>
        </Space>
      </Flex>

      {contract.status === "signed" && (
        <Alert
          type="success"
          showIcon
          className="mb-3"
          message={t("Signed by both parties. The contract can no longer be changed.")}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card className="contract-paper">
            {contract.contractDetails ? (
              <div
                className="contract-paper__body ql-editor"
                // Sanitised on the server when saved and again when served.
                dangerouslySetInnerHTML={{ __html: contract.contractDetails }}
              />
            ) : (
              <Empty description={t("This contract has no text yet.")} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t("Signatures")} className="contract-signatures">
            {parties.map(party => {
              const signature = contract.signatures?.[party.key];
              const covered = signature && signature.documentHash === contract.documentHash;
              return (
                <div key={party.key} className="contract-signature">
                  <Text type="secondary">{party.label}</Text>
                  {signature ? (
                    <>
                      <img
                        src={signature.image}
                        alt={t("Signature of {{name}}", { name: signature.name })}
                        className="contract-signature__img"
                      />
                      <div>
                        <Text strong>{signature.name}</Text>
                      </div>
                      <Text type="secondary" className="contract-signature__meta">
                        {dayjs(signature.signedAt).format("DD.MM.YYYY HH:mm")}
                        {signature.method === "in-person" ? ` · ${t("signed in person")}` : ""}
                      </Text>
                      {!covered && (
                        <Alert
                          type="error"
                          showIcon
                          className="mt-2"
                          message={t("The text was changed after this signature.")}
                        />
                      )}
                    </>
                  ) : party.canSign && contract.contractDetails ? (
                    <div className="mt-2">
                      <Button type="primary" block onClick={() => setSigning(party)}>
                        {party.key === "employee" && party.inPerson
                          ? t("Sign in person")
                          : t("Sign")}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Text type="secondary" italic>
                        {t("Not signed yet")}
                      </Text>
                    </div>
                  )}
                </div>
              );
            })}
            {contract.status === "partially_signed" && permissions.update && contract.employee && (
              <Text type="secondary" className="contract-legal-note">
                {t("The employee can sign from their own account under Profile → My Contracts.")}
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      {editOpen && (
        <ContractFormModal
          open={editOpen}
          contract={contract}
          onClose={() => setEditOpen(false)}
          onSaved={fetchContract}
        />
      )}
      <SignContractModal
        open={Boolean(signing)}
        contract={contract}
        party={signing?.key}
        defaultName={signing?.defaultName}
        inPerson={signing?.key === "employee" && signing?.inPerson}
        onClose={() => setSigning(null)}
        onSigned={fetchContract}
      />
    </div>
  );
};

export default ContractDetails;
